import React from "react";
import {
  Box,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  Typography,
  Stack,
  Divider,
  useTheme,
} from "@mui/material";
import ImageOrderingEditor, {
  ImageOrderingValue,
} from "../ImageOrderingEditor";
import TagMapEditor from "../TagMapEditor";
import AddTagMapButton from "../AddTagMapButton";
import { suggestionsForMap } from "../standardTags";
import SyndicatorCard from "../SyndicatorCard";

interface Props {
  config: any;
  onChange: (field: string, value: any) => void;
}

const DEFAULT_TAGMAPS = [
  "exterior_TagMap",
  "manual_TagMap",
  "featured_TagMap",
  "pano_TagMap",
];

export const auctionEdgeDefaults = {
  syndicatorData: {
    allowLocalReconCalculation: false,
    postRecon: false,
    apiUrl: "",
    encoding: "",
    auctionCode: "",
    assetLocationCodes: {
      exterior_TagMap: {},
      manual_TagMap: {},
      featured_TagMap: {},
      pano_TagMap: {},
    },
  },
  imageOrdering: {
    ImageOrder: "",
  },
};

const AuctionEdgeForm: React.FC<Props> = ({ config, onChange }) => {
  const theme = useTheme();
  const accent = theme.palette.status.accent;

  const syndicatorData = config.syndicatorData || {};
  const assetLocationCodes = syndicatorData.assetLocationCodes || {};
  const tagMapNames = Array.from(
    new Set([
      ...DEFAULT_TAGMAPS,
      ...Object.keys(assetLocationCodes).filter((k) => k.endsWith("_TagMap")),
    ]),
  );
  const imageOrdering: ImageOrderingValue = config.imageOrdering || {};

  return (
    <SyndicatorCard title="AuctionEdge Configuration">
      <Stack spacing={3}>
        <Box>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1.5, color: accent, fontWeight: 600 }}
          >
            Syndicator Data
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Auction Code"
                fullWidth
                size="small"
                value={syndicatorData.auctionCode || ""}
                onChange={(e) =>
                  onChange("syndicatorData.auctionCode", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="API URL"
                fullWidth
                size="small"
                value={syndicatorData.apiUrl || ""}
                onChange={(e) =>
                  onChange("syndicatorData.apiUrl", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Encoding"
                fullWidth
                size="small"
                value={syndicatorData.encoding || ""}
                onChange={(e) =>
                  onChange("syndicatorData.encoding", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack direction="row" spacing={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={syndicatorData.postRecon ?? false}
                      onChange={(e) =>
                        onChange("syndicatorData.postRecon", e.target.checked)
                      }
                    />
                  }
                  label={
                    <Typography sx={{ fontWeight: 500 }}>Post Recon</Typography>
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={
                        syndicatorData.allowLocalReconCalculation ?? false
                      }
                      onChange={(e) =>
                        onChange(
                          "syndicatorData.allowLocalReconCalculation",
                          e.target.checked,
                        )
                      }
                    />
                  }
                  label={
                    <Typography sx={{ fontWeight: 500 }}>
                      Allow Local Recon Calc
                    </Typography>
                  }
                />
              </Stack>
            </Grid>
          </Grid>
        </Box>

        <Box>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1.5, color: accent, fontWeight: 600 }}
          >
            Asset Location Codes
          </Typography>
          <Stack spacing={2}>
            {tagMapNames.map((mapName) => {
              const isDefault = DEFAULT_TAGMAPS.includes(mapName);
              return (
                <TagMapEditor
                  key={mapName}
                  label={mapName}
                  value={assetLocationCodes[mapName]}
                  onChange={(next) =>
                    onChange(
                      `syndicatorData.assetLocationCodes.${mapName}`,
                      next,
                    )
                  }
                  onRemove={
                    isDefault
                      ? undefined
                      : () => {
                          const next = { ...assetLocationCodes };
                          delete next[mapName];
                          onChange("syndicatorData.assetLocationCodes", next);
                        }
                  }
                  keyLabel="Tag"
                  valueLabel="Location Code"
                  tagSuggestions={suggestionsForMap(mapName)}
                />
              );
            })}
            <AddTagMapButton
              existingNames={tagMapNames}
              onAdd={(name) =>
                onChange(`syndicatorData.assetLocationCodes.${name}`, {})
              }
            />
          </Stack>
        </Box>

        <Divider />

        <Box>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1.5, color: accent, fontWeight: 600 }}
          >
            Image Ordering
          </Typography>
          <ImageOrderingEditor
            value={imageOrdering}
            onChange={(next) => onChange("imageOrdering", next)}
          />
        </Box>
      </Stack>
    </SyndicatorCard>
  );
};

export default AuctionEdgeForm;
